import React, {useState, useRef, useEffect, ReactNode, RefObject} from 'react'
import {createPortal} from 'react-dom'
import {KTIcon} from '../_metronic/helpers'

interface PortalDropdownProps {
  toggleLabel?: ReactNode
  children: ReactNode
  offsetY?: number
  offsetX?: number
  className?: string
  triggerProps?: any
}

const PortalDropdown: React.FC<PortalDropdownProps> = ({
  toggleLabel = <KTIcon iconName="dots-vertical" className="p-0 fs-1 text-dark" iconType="solid" />,
  children,
  offsetY = 8,
  offsetX = 0,
  className = '',
  triggerProps = {},
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({top: 0, left: 0})
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + window.scrollY + offsetY,
        left: rect.left + window.scrollX + offsetX,
      })
    }
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      const clickedOutsideToggle = buttonRef.current && !buttonRef.current.contains(target)
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target)

      if (clickedOutsideToggle && clickedOutsideDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="btn btn-light btn-sm"
        type="button"
        {...triggerProps}
      >
        {toggleLabel}
      </button>

      {createPortal(
        <div
          ref={dropdownRef}
          className={`dropdown-menu ${isOpen ? 'show' : ''} ${className}`}
          style={{
            position: 'absolute',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 1050,
            display: isOpen ? 'block' : 'none',
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  )
}

export default PortalDropdown
